import React, { useEffect, useState } from 'react';
import Navbar from './Navbar';
import { X, Instagram, Globe, Music, ExternalLink } from 'lucide-react';
import './App.css';
import './Explore.css';

interface Album {
  albumId: string;
  title: string;
  artist: string;
  artwork: string;
  cardImage: string;
  cardDescription: string;
  socialLinks: Record<string, string>;
  printCount: number;
  rarity: string;
  externalUrl: string;
  createdAt: string;
}

const API_BASE = process.env.REACT_APP_API_URL || '';

function getRarityClass(rarity: string): string {
  switch (rarity?.toLowerCase().replace(/\s+/g, '-')) {
    case 'very-rare': return 'very-rare';
    case 'rare': return 'rare';
    case 'uncommon': return 'uncommon';
    default: return 'common';
  }
}

function getSocialIcon(platform: string) {
  switch (platform.toLowerCase()) {
    case 'instagram': return <Instagram size={18} />;
    case 'spotify': return <Music size={18} />;
    case 'website': return <Globe size={18} />;
    default: return <ExternalLink size={18} />;
  }
}

function Explore() {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/explore/albums`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setAlbums(data.albums);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch explore albums:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selectedAlbum) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedAlbum]);

  return (
    <div className="App info-page">
      <Navbar />
      <div className="explore-content">
        <div className="explore-header">
          <h1>Explore</h1>
          <p>Discover album cards from artists around the world.</p>
        </div>

        {loading && (
          <div className="explore-loading">Loading...</div>
        )}

        {!loading && albums.length === 0 && (
          <div className="explore-empty">No cards to explore yet.</div>
        )}

        {!loading && albums.length > 0 && (
          <div className="explore-grid">
            {albums.map((album) => (
              <div
                key={album.albumId}
                className="explore-card"
                onClick={() => setSelectedAlbum(album)}
              >
                <img
                  src={album.cardImage || album.artwork}
                  alt={album.title}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}

        {selectedAlbum && (
          <div
            className="explore-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setSelectedAlbum(null);
              }
            }}
          >
            <div className="explore-detail">
              <button
                className="explore-detail-close"
                onClick={() => setSelectedAlbum(null)}
                aria-label="Close"
              >
                <X size={20} />
              </button>

              <img
                className="explore-detail-image"
                src={selectedAlbum.cardImage || selectedAlbum.artwork}
                alt={selectedAlbum.title}
              />

              <h2 className="explore-detail-title">{selectedAlbum.title}</h2>
              <p className="explore-detail-artist">{selectedAlbum.artist}</p>

              {selectedAlbum.printCount > 0 && (
                <p className="explore-detail-print-count">
                  {selectedAlbum.printCount} {selectedAlbum.printCount === 1 ? 'print' : 'prints'}
                </p>
              )}

              {selectedAlbum.cardDescription && (
                <p className="explore-detail-description">
                  {selectedAlbum.cardDescription}
                </p>
              )}

              {selectedAlbum.socialLinks && Object.keys(selectedAlbum.socialLinks).length > 0 && (
                <div className="explore-detail-socials">
                  {Object.entries(selectedAlbum.socialLinks).map(([platform, url]) => (
                    url ? (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={platform}
                      >
                        {getSocialIcon(platform)}
                      </a>
                    ) : null
                  ))}
                </div>
              )}

              {selectedAlbum.externalUrl && (
                <a
                  className="explore-detail-link"
                  href={selectedAlbum.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Listen / Buy
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Explore;
